/**
 * 统一的API请求工具
 *
 * 功能：
 * - 自动添加认证头
 * - 处理401未授权响应（令牌过期）
 * - 统一错误处理
 */

import { message } from 'antd'

// API 基础地址
// Docker 环境：使用空字符串（通过 Vite 代理）
// 生产环境：使用完整 URL
const API_BASE = import.meta.env.VITE_API_BASE || ''

/** 请求配置 */
export interface RequestOptions extends RequestInit {
  /** 是否需要认证（默认true） */
  auth?: boolean
  /** 是否显示错误提示（默认true） */
  showError?: boolean
}

/**
 * 统一的fetch请求封装
 *
 * @param url - 请求URL（相对路径或绝对路径）
 * @param options - 请求配置
 * @returns 响应数据
 */
export async function request<T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    auth = true,
    showError = true,
    headers = {},
    ...restOptions
  } = options

  // 构建完整URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`

  // 构建请求头
  const requestHeaders = new Headers(headers)
  requestHeaders.set('Content-Type', 'application/json')

  // 添加认证令牌
  if (auth) {
    const token = getToken()
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }
  }

  try {
    const response = await fetch(fullUrl, {
      ...restOptions,
      headers: requestHeaders,
    })

    // 处理401未授权（令牌过期）
    if (response.status === 401) {
      handleUnauthorized()
      throw new Error('登录已过期，请重新登录')
    }

    // 处理其他HTTP错误
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.detail || errorData.message || `请求失败 (${response.status})`
      throw new Error(errorMessage)
    }

    // 解析JSON响应
    const data = await response.json()
    return data
  } catch (error) {
    // 显示错误提示
    if (showError && error instanceof Error) {
      message.error(error.message)
    }
    throw error
  }
}

/**
 * GET 请求
 */
export async function get<T = any>(
  url: string,
  options?: RequestOptions
): Promise<T> {
  return request<T>(url, { ...options, method: 'GET' })
}

/**
 * POST 请求
 */
export async function post<T = any>(
  url: string,
  data?: any,
  options?: RequestOptions
): Promise<T> {
  return request<T>(url, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * PUT 请求
 */
export async function put<T = any>(
  url: string,
  data?: any,
  options?: RequestOptions
): Promise<T> {
  return request<T>(url, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * DELETE 请求
 */
export async function del<T = any>(
  url: string,
  options?: RequestOptions
): Promise<T> {
  return request<T>(url, { ...options, method: 'DELETE' })
}

/**
 * 从 localStorage 获取令牌
 */
function getToken(): string | null {
  try {
    const userStorage = localStorage.getItem('user-storage')
    if (!userStorage) return null

    const parsed = JSON.parse(userStorage)
    return parsed.state?.token || null
  } catch {
    return null
  }
}

/**
 * 处理未授权响应（令牌过期）
 *
 * 清除本地存储并跳转到登录页
 */
function handleUnauthorized(): void {
  // 清除用户存储
  localStorage.removeItem('user-storage')

  // 显示提示
  message.warning('登录已过期，请重新登录')

  // 跳转到登录页
  window.location.href = '/login'
}
